import { useEffect, useMemo, useState } from "react";
import NiceModal, { useModal } from "@ebay/nice-modal-react";
import Select from "react-select";
import SideModal from "/src/components/modal/SideModal/SideModal";
import useModalHelper from "/src/components/modal/hooks/useModalHelper";
import Form from "/src/components/form/Form";
import Field from "/src/components/UI/Field/Field";
import TextInput from "/src/components/UI/TextField/TextInput";
import TextArea from "/src/components/UI/TextField/TextArea";
import Button from "/src/components/UI/Button/Button";
import { useForm } from "react-hook-form";
import poleAdminService from "../../services/poleAdminService";
import userService from "/src/features/administration/user/services/userService";
import { useToastDispatchContext } from "/src/layouts/toast/context/toastContext";
import "./PoleModal.css";

function parseErrorDetail(error, fallback) {
    try {
        return JSON.parse(error?.body)?.detail || fallback;
    } catch {
        return fallback;
    }
}

const selectStyles = {
    option: (base, state) => ({
        ...base,
        backgroundColor: state.isSelected ? "var(--secondary)" : base.backgroundColor,
        color: state.isSelected ? "var(--main)" : base.color,
    }),
    menu: (base) => ({ ...base, zIndex: 5 }),
};

function MultiUserSelect({ options, selectedIds, onChange, noun }) {
    return (
        <div className="pole-form-multiselect">
            <Select
                classNamePrefix="pole-select"
                isMulti
                options={options}
                value={options.filter((o) => selectedIds.includes(o.value))}
                onChange={(selected) => onChange(selected ? selected.map((o) => o.value) : [])}
                hideSelectedOptions={false}
                controlShouldRenderValue={false}
                closeMenuOnSelect={false}
                styles={selectStyles}
                placeholder={
                    selectedIds.length
                        ? `${selectedIds.length} ${noun}${selectedIds.length > 1 ? "s" : ""} selected`
                        : `No ${noun}s assigned...`
                }
            />

            {selectedIds.length > 0 && (
                <div className="pole-form-multiselect_selected">
                    {selectedIds.map((id) => {
                        const option = options.find((o) => o.value === id) || { value: id, label: id };
                        return (
                            <span
                                key={id}
                                className="pole-form-agent-chip"
                                title="Click to remove"
                                onClick={() => onChange(selectedIds.filter((selectedId) => selectedId !== id))}
                            >
                                {option.label}
                                <i className="fas fa-times pole-form-agent-chip_remove"></i>
                            </span>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

const PoleModal = NiceModal.create(({ title, data }) => {
    const modal = useModal();
    const { onClose } = useModalHelper(modal);
    const toast = useToastDispatchContext();
    const { createPole, updatePole } = poleAdminService;
    const { getPoles } = poleAdminService;

    const { register, handleSubmit, formState: { errors, isValid } } = useForm({
        mode: "onChange",
        defaultValues: { name: data?.name || "", description: data?.description || "" },
    });

    const [users, setUsers] = useState([]);
    // A leader can lead several poles and a pole can have several leaders,
    // so being a leader elsewhere is not by itself a reason to hide someone
    // from the Leaders list - only being an agent elsewhere is (leader/agent
    // are mutually exclusive). Conversely, leading ANY other pole blocks
    // becoming an agent. Both maps exclude the pole being edited - its own
    // current leaders/agents are handled live via leaderIds/agentIds below.
    const [agentElsewhereByUserId, setAgentElsewhereByUserId] = useState({});
    const [leaderElsewhereByUserId, setLeaderElsewhereByUserId] = useState({});
    const [leaderIds, setLeaderIds] = useState((data?.leaders || []).map((l) => l.id));
    const [agentIds, setAgentIds] = useState((data?.agents || []).map((a) => a.id));

    useEffect(() => {
        const fetchOptions = async () => {
            const [usersResponse] = await userService.getUsers({ page: 1, limit: -1 });
            setUsers(usersResponse?.data || []);

            const [polesResponse] = await getPoles();
            const agentElsewhere = {};
            const leaderElsewhere = {};
            (polesResponse || []).forEach((pole) => {
                if (pole.id === data?.id) return;
                (pole.leaders || []).forEach((l) => { leaderElsewhere[l.id] = pole.name; });
                (pole.agents || []).forEach((a) => { agentElsewhere[a.id] = pole.name; });
            });
            setAgentElsewhereByUserId(agentElsewhere);
            setLeaderElsewhereByUserId(leaderElsewhere);
        };
        fetchOptions();
    }, []);

    const userOptions = useMemo(() => users.map((u) => ({ value: u.id, label: u.email })), [users]);

    // A user can't be both a leader and an agent of the same pole either -
    // exclude whoever is currently picked (live) in the other field, on top
    // of hiding users already tied to another pole.
    const leaderOptions = useMemo(() =>
        userOptions.filter((o) => !agentElsewhereByUserId[o.value] && !agentIds.includes(o.value)),
    [userOptions, agentElsewhereByUserId, agentIds]);

    const agentOptions = useMemo(() =>
        userOptions.filter((o) =>
            !agentElsewhereByUserId[o.value] && !leaderElsewhereByUserId[o.value] && !leaderIds.includes(o.value)
        ),
    [userOptions, agentElsewhereByUserId, leaderElsewhereByUserId, leaderIds]);

    const onSubmit = async (values) => {
        const payload = { ...values, leader_user_ids: leaderIds, agent_user_ids: agentIds };

        const [response, error] = data
            ? await updatePole(data.id, payload)
            : await createPole(payload);

        if (error) {
            toast.error("Error", parseErrorDetail(error, "Failed to save the pole."), 5000);
            return;
        }
        modal.resolve(response);
        onClose();
    };

    return (
        <SideModal name="pole-modal" id={modal.id} autoClose="true" onClose={onClose} style={{ width: "500px" }}>
            <div className="button-leave" id="close_modal_container" onClick={() => onClose()}>
                <i id="close_modal" className="fa fa-times" aria-hidden="true"></i>
            </div>
            <Form className="pole-form" onSubmit={handleSubmit(onSubmit)}>
                <Form.Title>{title}</Form.Title>
                <Form.Body className="pole-form-body">
                    <Field label={<span>Name <span className="obligatory_field">*</span></span>}>
                        <TextInput {...register("name", { required: true })} />
                        <span className="error-display">{errors.name ? "Name is required" : ""}</span>
                    </Field>

                    <Field label="Description">
                        <TextArea {...register("description")} style={{ height: "60px" }} />
                    </Field>

                    <Field label="Leaders">
                        <MultiUserSelect options={leaderOptions} selectedIds={leaderIds} onChange={setLeaderIds} noun="leader" />
                    </Field>

                    <Field label="Agents">
                        <MultiUserSelect options={agentOptions} selectedIds={agentIds} onChange={setAgentIds} noun="agent" />
                    </Field>
                </Form.Body>
                <Form.Buttons className="pole-form-buttons">
                    <Button type="submit" label="Save" className="validate" disabled={!isValid} />
                </Form.Buttons>
            </Form>
        </SideModal>
    );
});

export default PoleModal;
